import { GridOverlay } from '@mui/x-data-grid';
import { createElement } from 'react';
import { componentSizes } from '@/theme/componentSizes';
import { s, tableTokens } from '@/theme/tokens';
import { Box } from './Box';
import { DataGridEmptyState } from './dataGridStates';
import { Skeleton } from './Skeleton';
import type { GridOverlayProps } from '@mui/x-data-grid';
import type { ReactElement } from 'react';

type DataGridStateOptions = {
  emptyTitle?: string;
  emptyBody?: string;
  error?: boolean;
  errorTitle?: string;
  errorBody?: string;
  errorAction?: ReactElement;
};

type DataGridDefaults = {
  pageSizeOptions: number[];
  initialState: {
    pagination: { paginationModel: { page: number; pageSize: number } };
    sorting: { sortModel: [] };
  };
  slots: {
    noRowsOverlay: () => ReactElement;
    noResultsOverlay: () => ReactElement;
    loadingOverlay: (props: GridOverlayProps) => ReactElement;
  };
  slotProps: { loadingOverlay: { variant: 'skeleton'; noRowsVariant: 'skeleton' } };
};

const renderEmptyState = (props: {
  title: string;
  body?: string;
  action?: ReactElement;
}): ReactElement => createElement(DataGridEmptyState, props);

const overlayRowCount = 6;

const DataGridSkeletonOverlay = (props: GridOverlayProps): ReactElement => {
  const skeletonRows = Array.from({ length: overlayRowCount }, (_value, index) =>
    createElement(
      Box,
      {
        key: `grid-overlay-skeleton-${index}`,
        sx: {
          display: 'flex',
          alignItems: 'center',
          py: s(tableTokens.cellPadding.y),
        },
      },
      createElement(Skeleton, {
        height: componentSizes.dataGrid.skeletonHeight,
        width: '100%',
        variant: 'text',
      }),
    ),
  );
  return createElement(
    GridOverlay,
    props,
    createElement(Box, { sx: { width: '100%', px: s(tableTokens.cellPadding.x) } }, skeletonRows),
  );
};

export const getDataGridDefaults = (options: DataGridStateOptions = {}): DataGridDefaults => {
  const {
    emptyTitle = 'No results',
    emptyBody = 'Try adjusting filters or search.',
    error = false,
    errorTitle = 'Something went wrong',
    errorBody = 'Refresh or try again later.',
    errorAction,
  } = options;

  const overlayProps = error
    ? { title: errorTitle, body: errorBody, action: errorAction }
    : { title: emptyTitle, body: emptyBody };

  return {
    pageSizeOptions: [5, 10, 25, 50],
    initialState: {
      pagination: { paginationModel: { page: 0, pageSize: 25 } },
      sorting: { sortModel: [] },
    },
    slots: {
      noRowsOverlay: () => renderEmptyState(overlayProps),
      noResultsOverlay: () => renderEmptyState(overlayProps),
      loadingOverlay: DataGridSkeletonOverlay,
    },
    slotProps: { loadingOverlay: { variant: 'skeleton', noRowsVariant: 'skeleton' } },
  };
};

export type { DataGridDefaults, DataGridStateOptions };
